import {announce} from '@github-ui/aria-live'
import type {
  WorkflowDispatchDefinitionResponse,
  WorkflowDispatchInput,
} from '@github-ui/dotcom-schema/types/actions/v1/workflow_dispatch_definition'
import {WorkflowDispatchInputType} from '@github-ui/dotcom-schema/types/actions/v1/workflow_dispatch_definition'
import {RefSelector, type RefType} from '@github-ui/ref-selector'
import {ResponseError} from '@github-ui/react-core/future/response-error'
import {useMutation} from '@github-ui/react-query'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {AlertIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Banner, Button, Checkbox, FormControl, Link, Select, Spinner, TextInput} from '@primer/react'
import {Blankslate, InlineMessage} from '@primer/react/experimental'
import {useCallback, useId, useRef, useState, type FormEvent, type ReactNode, type RefObject} from 'react'

import styles from './WorkflowDispatchMenu.module.css'
import {useClickAnalytics} from '@github-ui/use-analytics'

interface WorkflowDispatchDialogProps {
  definition?: WorkflowDispatchDefinitionResponse
  error: boolean
  isFetching: boolean
  isOpen: boolean
  onClose: () => void
  onRefSelectorOpenChange: (open: boolean) => void
  onRetry: () => void
  onSelectRef: (ref: string, refType: RefType) => void
  onSubmit: () => void
  owner: string
  repo: string
  returnFocusRef: RefObject<HTMLButtonElement | null>
  selectedRef: string
  selectedRefType: RefType
}

function inputName(name: string): string {
  return `inputs[${name}]`
}

export async function dispatchWorkflow(path: string, formData: FormData) {
  const response = await verifiedFetch(path, {method: 'POST', body: formData})
  if (response.redirected) return response.url
  if (!response.ok) throw new ResponseError('Workflow could not be run.', response)

  throw new Error('Workflow dispatch did not redirect.')
}

function isNumber(value: string): boolean {
  return value.trim() === '' || Number.isFinite(Number(value))
}

function WorkflowDispatchNumberInputControl({
  caption,
  defaultValue,
  label,
  name,
  required,
}: {
  caption?: string
  defaultValue: string
  label: string
  name: string
  required: boolean
}) {
  const [showWarning, setShowWarning] = useState(() => !isNumber(defaultValue))

  const validateValue = useCallback(
    (value: string) => {
      const nextShowWarning = !isNumber(value)
      if (nextShowWarning && !showWarning) announce('This input expects a number.')
      setShowWarning(nextShowWarning)
    },
    [showWarning],
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (showWarning && isNumber(event.currentTarget.value)) setShowWarning(false)
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    validateValue(event.currentTarget.value)
  }

  return (
    <FormControl required={required}>
      <FormControl.Label>{label}</FormControl.Label>
      <TextInput
        block
        name={name}
        type="text"
        inputMode="decimal"
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {(caption || showWarning) && (
        <FormControl.Caption>
          {caption}
          {showWarning && (
            <InlineMessage variant="warning" size="small">
              This input expects a number.
            </InlineMessage>
          )}
        </FormControl.Caption>
      )}
    </FormControl>
  )
}

function WorkflowDispatchInputControl({
  input,
  owner,
  repo,
}: {
  input: WorkflowDispatchInput
  owner: string
  repo: string
}) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const name = inputName(input.name)
  const label = input.description || input.name
  const caption = input.description ? input.name : undefined

  if (input.type === WorkflowDispatchInputType.BOOLEAN) {
    return (
      <>
        <input type="hidden" name={name} value="false" />
        <FormControl>
          <Checkbox name={name} value="true" defaultChecked={input.defaultValue === 'true'} />
          <FormControl.Label>{label}</FormControl.Label>
          {caption && <FormControl.Caption>{caption}</FormControl.Caption>}
        </FormControl>
      </>
    )
  }

  if (input.type === WorkflowDispatchInputType.CHOICE || input.type === WorkflowDispatchInputType.ENVIRONMENT) {
    const hasNoEnvironments = input.type === WorkflowDispatchInputType.ENVIRONMENT && input.options.length === 0

    return (
      <FormControl required={input.required} disabled={hasNoEnvironments}>
        <FormControl.Label>{label}</FormControl.Label>
        <Select name={name} defaultValue={input.defaultValue || input.options[0] || ''} block>
          {input.options.map(option => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
        {hasNoEnvironments ? (
          <FormControl.Caption>
            No environments are configured.{' '}
            <Link
              href={`/${owner}/${repo}/settings/environments`}
              inline
              onClick={() =>
                sendClickAnalyticsEvent({
                  category: 'ACTIONS_WORKFLOW_DISPATCH',
                  action: 'workflow_dispatch.configure_environments',
                  label: 'ref_loc:workflow_dispatch_dialog',
                })
              }
            >
              Configure environments
            </Link>
          </FormControl.Caption>
        ) : (
          caption && <FormControl.Caption>{caption}</FormControl.Caption>
        )}
      </FormControl>
    )
  }

  if (input.type === WorkflowDispatchInputType.NUMBER) {
    return (
      <WorkflowDispatchNumberInputControl
        caption={caption}
        defaultValue={input.defaultValue}
        label={label}
        name={name}
        required={input.required}
      />
    )
  }

  return (
    <FormControl required={input.required}>
      <FormControl.Label>{label}</FormControl.Label>
      <TextInput block name={name} type="text" defaultValue={input.defaultValue} />
      {caption && <FormControl.Caption>{caption}</FormControl.Caption>}
    </FormControl>
  )
}

function focusBannerOnMount(element: HTMLElement | null) {
  if (!element) return

  const animationFrame = requestAnimationFrame(() => element.focus())
  return () => cancelAnimationFrame(animationFrame)
}

/** Renders the lazy workflow-dispatch form in Primer's right-side Dialog variation. */
export function WorkflowDispatchDialog({
  definition,
  error,
  isFetching,
  isOpen,
  onClose,
  onRefSelectorOpenChange,
  onRetry,
  onSelectRef,
  onSubmit,
  owner,
  repo,
  returnFocusRef,
  selectedRef,
  selectedRefType,
}: WorkflowDispatchDialogProps) {
  const formId = useId()
  const refSelectorId = useId()
  const refSelectorButtonId = `ref-picker-${refSelectorId}`
  const contentRef = useRef<HTMLDivElement>(null)
  const submissionInFlightRef = useRef(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const dispatchMutation = useMutation({
    mutationFn: (formData: FormData) => {
      if (!definition) throw new Error('Workflow dispatch definition is unavailable.')
      return dispatchWorkflow(definition.triggerPath, formData)
    },
    onMutate: onSubmit,
    onSuccess: (redirectUrl: string) => {
      setIsRedirecting(true)
      window.location.assign(redirectUrl)
    },
    onError: () => {
      submissionInFlightRef.current = false
    },
  })

  const isSubmitting = dispatchMutation.isPending || isRedirecting
  const isBusy = isFetching || isSubmitting
  const canSubmit = Boolean(definition && !isFetching && !error && definition.workflowValid)
  const handleRetry = useCallback(() => {
    contentRef.current?.focus()
    onRetry()
  }, [onRetry])
  const hasDefinitionError = error && !isFetching
  const hasInvalidWorkflow = Boolean(definition && !isFetching && !error && !definition.workflowValid)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!definition?.workflowValid || submissionInFlightRef.current) return

    submissionInFlightRef.current = true
    dispatchMutation.mutate(new FormData(event.currentTarget))
  }

  const handleClose = () => {
    if (isSubmitting) {
      onClose()
      return
    }

    submissionInFlightRef.current = false
    setIsRedirecting(false)
    dispatchMutation.reset()
    onClose()
  }

  return (
    <Overlay
      onClose={handleClose}
      anchorRef={returnFocusRef}
      formId={formId}
      canSubmit={canSubmit}
      isSubmitting={isSubmitting}
      dispatch={dispatchMutation}
      isOpen={isOpen}
    >
      <div
        ref={contentRef}
        className={styles.Content}
        aria-busy={isBusy}
        tabIndex={-1}
        data-testid="workflow-dispatch-dialog-content"
      >
        <FormControl id={refSelectorButtonId} className={styles.RefSelector} disabled={isSubmitting}>
          <FormControl.Label>Use workflow from</FormControl.Label>
          <RefSelector
            ariaLabel={`Use workflow from: ${selectedRef}`}
            cacheKey=""
            canCreate={false}
            closeOnSelect
            currentCommitish={selectedRef}
            defaultBranch={definition?.defaultRef || selectedRef}
            disabled={isSubmitting}
            idEnding={refSelectorId}
            onOpenChange={onRefSelectorOpenChange}
            onSelectItem={onSelectRef}
            owner={owner}
            repo={repo}
            selectedRefType={selectedRefType}
            types={['branch', 'tag']}
          />
        </FormControl>
        <div className={styles.FormContainer}>
          {isFetching && (
            <div className={styles.LoadingOverlay} role="status">
              <Spinner srText="Loading workflow details." />
            </div>
          )}
          <div className={isFetching ? styles.LoadingContent : undefined}>
            {hasDefinitionError && (
              <Blankslate narrow>
                <Blankslate.Visual>
                  <AlertIcon size="medium" />
                </Blankslate.Visual>
                <Blankslate.Heading>Workflow details could not be loaded.</Blankslate.Heading>
                <Blankslate.Description>Try loading the workflow details again.</Blankslate.Description>
                <Blankslate.PrimaryAction onClick={handleRetry}>Retry</Blankslate.PrimaryAction>
              </Blankslate>
            )}

            {dispatchMutation.isError && (
              <Banner
                ref={focusBannerOnMount}
                tabIndex={-1}
                className={styles.Banner}
                variant="critical"
                title="Workflow dispatch failed"
                description="Workflow could not be run. Please try again."
              />
            )}

            {hasInvalidWorkflow && (
              <Blankslate narrow>
                <Blankslate.Visual>
                  <AlertIcon size="medium" />
                </Blankslate.Visual>
                <Blankslate.Heading>Workflow dispatch unavailable</Blankslate.Heading>
                <Blankslate.Description>
                  Workflow does not exist or does not have a <code>workflow_dispatch</code> trigger in this{' '}
                  {selectedRefType}.
                </Blankslate.Description>
              </Blankslate>
            )}

            {definition && !error && definition.workflowValid && (
              <form
                id={formId}
                action={definition.triggerPath}
                method="post"
                onSubmit={handleSubmit}
                aria-label="Workflow dispatch"
              >
                <input type="hidden" name="workflow" value={definition.workflowPath} />
                <input type="hidden" name={selectedRefType} value={selectedRef} />
                <input type="hidden" name="show_workflow_tip" value={definition.showWorkflowTip ? '1' : ''} />
                <fieldset className={styles.Fieldset} disabled={isBusy}>
                  <div className={styles.InputList} role="group" aria-label="Workflow inputs">
                    {definition.inputs.map(input => (
                      <WorkflowDispatchInputControl
                        // - we want to re-mount the input if the name OR value
                        //   changes to address this bug:
                        //   https://github.com/github/core-ux/issues/3983
                        key={`${input.name}:${input.defaultValue}`}
                        input={input}
                        owner={owner}
                        repo={repo}
                      />
                    ))}
                  </div>
                </fieldset>
              </form>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  )
}

function Overlay({
  onClose,
  anchorRef,
  formId,
  canSubmit,
  isSubmitting,
  dispatch,
  isOpen,
  children,
}: Pick<WorkflowDispatchDialogProps, 'onClose' | 'isOpen'> & {
  anchorRef: WorkflowDispatchDialogProps['returnFocusRef']
  formId: string
  canSubmit: boolean
  isSubmitting: boolean
  dispatch: {isError: boolean}
  children: ReactNode
}) {
  return (
    <AnchoredOverlay
      open={isOpen}
      onClose={onClose}
      renderAnchor={null}
      anchorRef={anchorRef}
      align="end"
      width="medium"
      height="auto"
      preventOverflow={false}
      focusZoneSettings={{disabled: true}}
      overlayProps={{role: 'dialog', 'aria-label': 'Run workflow', 'aria-modal': true}}
    >
      <div className={styles.Overlay}>
        {children}
        <div className={styles.OverlayFooter}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={!canSubmit || isSubmitting}
            loading={isSubmitting}
            loadingAnnouncement="Running workflow"
            aria-busy={isSubmitting}
          >
            {dispatch.isError ? 'Retry run' : 'Run workflow'}
          </Button>
        </div>
      </div>
    </AnchoredOverlay>
  )
}
